import type { PersistedActiveIntent } from './activeIntent';
import { withStage } from './activeIntent';
import {
  captureModeOf,
  HOLD_NOT_ACCEPTED_CODE,
  isAcceptedPayment,
  isCaptureReady,
  isRefusedWith,
  isTerminalFailure,
  isTerminalSuccess,
  type PaymentIntentDto,
  UNKNOWN_INTENT_CODE,
} from './paymentStatus';

interface CaptureReconcilerObserver {
  recordEvent(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
  recordException(error: unknown, tag: string): void;
}

export type CaptureResolution =
  | { kind: 'settled'; paymentId: string }
  | { kind: 'cleared-failure' }
  | { kind: 'preserved' };

interface AcceptPaymentInput {
  paymentIntentId: string;
  sessionId: string;
  partnerStripeId: string | null;
  // Recovery must carry the order identity too. Omitting it here blanks the
  // key the create recorded, and every later purchase on the session is
  // refused — the original ENTR fault, reached through the reload path.
  checkoutKey?: string;
}

export interface CaptureReconcilerDeps {
  checkPaymentIntentStatus(input: {
    paymentIntentId: string;
    sessionId: string;
    partnerStripeId: string | null;
  }): Promise<PaymentIntentDto>;
  capturePaymentIntent(input: AcceptPaymentInput): Promise<PaymentIntentDto>;
  // A repeated hold step is safe: the backend only records the acceptance.
  holdPaymentIntent(input: AcceptPaymentInput): Promise<PaymentIntentDto>;
  preserveIntentIfCurrent(intent: PersistedActiveIntent): void;
  clearIntentEverywhere(intent: PersistedActiveIntent): void;
  // The create of the same order returns a cancelled intent again (the same
  // Stripe idempotency key, and Stripe replays the first answer).
  rememberCanceledIntent(paymentIntentId: string): void;
  observer?: CaptureReconcilerObserver;
}

export interface CaptureReconciler {
  reconcileCaptureIntent(
    intent: PersistedActiveIntent,
    allowCapture: boolean
  ): Promise<CaptureResolution>;
  // The hold step for a capture after print, else the capture.
  acceptPayment(intent: PersistedActiveIntent): Promise<PaymentIntentDto>;
}

export function createCaptureReconciler(
  deps: CaptureReconcilerDeps
): CaptureReconciler {
  function acceptPayment(
    intent: PersistedActiveIntent
  ): Promise<PaymentIntentDto> {
    const input: AcceptPaymentInput = {
      paymentIntentId: intent.paymentIntentId,
      sessionId: intent.sessionId,
      partnerStripeId: intent.partnerStripeId ?? null,
      checkoutKey: intent.checkoutKey,
    };
    return intent.capture === 'after_print'
      ? deps.holdPaymentIntent(input)
      : deps.capturePaymentIntent(input);
  }

  async function reconcileCaptureIntent(
    intent: PersistedActiveIntent,
    allowCapture: boolean
  ): Promise<CaptureResolution> {
    const captureIntent = withStage(intent, 'capture');
    deps.preserveIntentIfCurrent(captureIntent);

    try {
      const currentIntent = await deps.checkPaymentIntentStatus({
        paymentIntentId: captureIntent.paymentIntentId,
        sessionId: captureIntent.sessionId,
        partnerStripeId: captureIntent.partnerStripeId ?? null,
      });

      if (isTerminalSuccess(currentIntent.status)) {
        deps.clearIntentEverywhere(captureIntent);
        return { kind: 'settled', paymentId: currentIntent.id };
      }

      if (isTerminalFailure(currentIntent.status)) {
        deps.rememberCanceledIntent(captureIntent.paymentIntentId);
        deps.clearIntentEverywhere(captureIntent);
        return { kind: 'cleared-failure' };
      }

      // A held amount is not paid for the kiosk before the hold step answers.
      if (!isCaptureReady(currentIntent.status) || !allowCapture) {
        deps.preserveIntentIfCurrent(captureIntent);
        return { kind: 'preserved' };
      }

      const capture = captureModeOf(
        captureIntent.capture,
        currentIntent.capture
      );
      const settledIntent = await acceptPayment({ ...captureIntent, capture });

      if (isAcceptedPayment(settledIntent, capture)) {
        deps.clearIntentEverywhere(captureIntent);
        return { kind: 'settled', paymentId: settledIntent.id };
      }

      if (isTerminalFailure(settledIntent.status)) {
        deps.rememberCanceledIntent(captureIntent.paymentIntentId);
        deps.clearIntentEverywhere(captureIntent);
        return { kind: 'cleared-failure' };
      }

      deps.preserveIntentIfCurrent(captureIntent);
      return { kind: 'preserved' };
    } catch (error) {
      // Stripe has no such intent, thus it holds no money of the visitor. A
      // kept intent stops each next payment at this same reconcile.
      if (isRefusedWith(error, UNKNOWN_INTENT_CODE)) {
        deps.observer?.recordEvent(
          'entr.payment',
          'capture intent is not on the Stripe account',
          {
            // Full Stripe IDs are intentional telemetry join keys.
            payment_intent_id: captureIntent.paymentIntentId,
          }
        );
        deps.rememberCanceledIntent(captureIntent.paymentIntentId);
        deps.clearIntentEverywhere(captureIntent);
        return { kind: 'cleared-failure' };
      }
      // A hold step on a closed hold is an expected answer. The next reconcile
      // reads the status of the intent.
      if (!isRefusedWith(error, HOLD_NOT_ACCEPTED_CODE)) {
        deps.observer?.recordException(
          error,
          'stripe_terminal_capture_reconcile'
        );
      }
      deps.observer?.recordEvent('entr.payment', 'capture reconcile failed', {
        // Full Stripe IDs are intentional telemetry join keys.
        payment_intent_id: captureIntent.paymentIntentId,
        error: error instanceof Error ? error.message : String(error),
      });
      deps.preserveIntentIfCurrent(captureIntent);
      return { kind: 'preserved' };
    }
  }

  return { reconcileCaptureIntent, acceptPayment };
}
